import VueCookies from 'vue-cookies'
import debounce from 'lodash/debounce'
import startsWith from 'lodash/startsWith'
import axios from 'axios'

import jwt from '@/helpers/jwt'
import * as Types from '@/store/types'

const Bearer = 'Bearer '

export default
{
	state:
	{
		timer: null,

		errors:
		{
			username: null,
			password: null,
		},
	},

	mutations:
	{
		[Types.LOGIN_SUCCESS](state, payload)
		{
			state.errors.username = null
			state.errors.password = null
		},

		[Types.LOGIN_FAILURE](state, payload)
		{
			state.errors.username = payload?.errors?.username?.[0]
			state.errors.password = payload?.errors?.password?.[0]
		},

		[Types.CLEAR_LOGIN_ERRORS](state, payload)
		{
			state.errors.username = null
			state.errors.password = null
		},

		[Types.TOKEN_TIMER](state, payload)
		{
			if ( state.timer )
			{
				window.clearTimeout(state.timer)
			}

			state.timer = payload?.timer
		},
	},

	actions:
	{
		[Types.LOGIN]({ dispatch }, payload)
		{
			return new Promise
			(
				(resolve, reject) =>
				{
					axios.post('/api/auth/login', payload)
						.then
						(
							async response =>
							{
								const { data } = response

								const { token } = data ?? {}

								if ( token )
								{
									await dispatch(Types.LOGIN_SUCCESS, data)
								}

								resolve(response)
							}
						)
						.catch
						(
							error =>
							{
								dispatch(Types.LOGIN_FAILURE, error.response.data)

								reject(error)
							}
						)
				}
			)
		},

		[Types.LOGIN_SUCCESS]({ commit, dispatch }, payload)
		{
			commit({ type: Types.LOGIN_SUCCESS })

			return new Promise
			(
				(resolve, reject) =>
				{
					dispatch(Types.SET_TOKEN, payload)
						.then(resolve)
						.catch(reject)
				}
			)
		},

		[Types.LOGIN_FAILURE]({ commit, dispatch }, payload)
		{
			commit({ type: Types.LOGIN_FAILURE, errors: payload.errors || [] })

			if ( payload.error )
			{
				dispatch(Types.SHOW_ERROR_NOTIFICATION, payload.error)
			}
		},

		[Types.CLEAR_LOGIN_ERRORS]({ commit })
		{
			commit({ type: Types.CLEAR_LOGIN_ERRORS })
		},

		[Types.LOGOUT]({ dispatch }, payload)
		{
			return new Promise
			(
				(resolve, reject) =>
				{
					axios.post('/api/auth/logout')
						.then
						(
							async response =>
							{
								jwt.removeToken()

								await dispatch(Types.UNSET_AUTH_USER)

								resolve(response)
							}
						)
						.catch(reject)
				}
			)
		},

		[Types.EMPLOYEE_LOGIN]({ dispatch }, payload)
		{
			return new Promise
			(
				(resolve, reject) =>
				{
					axios.post('/api/auth/employee/login', payload)
						.then(resolve)
						.catch
						(
							error =>
							{
								dispatch(Types.LOGIN_FAILURE, error.response.data)

								reject(error)
							}
						)
				}
			)
		},

		[Types.EMPLOYEE_LOGOUT]({ dispatch })
		{
			return new Promise
			(
				(resolve, reject) =>
				{
					dispatch(Types.LOGOUT).then
					(
						() =>
						{
							axios.get('/api/auth/employee/logout')
								.then
								(
									response =>
									{
										jwt.removeEmployeeToken()
										dispatch(Types.UNSET_AUTH_EMPLOYEE)
										resolve(response)
									}
								)
								.catch(reject)
						}
					)
				}
			)
		},

		[Types.REFRESH_TOKEN]: debounce
		(
			({ dispatch }) =>
			{
				if ( jwt.hasExpiredToken() )
				{
					return Promise.resolve()
				}

				return new Promise
				(
					(resolve, reject) =>
					{
						axios.post('/api/auth/refresh')
							.then
							(
								async response =>
								{
									const { data } = response

									await dispatch(Types.SET_TOKEN, data)

									resolve(response)
								}
							)
							.catch(reject)
					}
				)
			},
			500
		),

		[Types.SET_TOKEN]({ dispatch }, payload)
		{
			const employeeToken = payload?.data?.employee_token ?? payload?.employee_token

			if ( employeeToken )
			{
				jwt.setEmployeeToken(employeeToken)

				return new Promise
				(
					(resolve, reject) =>
					{
						dispatch(Types.SET_AUTH_EMPLOYEE)
							.then(resolve)
							.catch(reject)
					}
				)
			}

			let token = payload?.token

			if ( token )
			{
				if ( startsWith(token, Bearer) )
				{
					token = token.slice(Bearer.length)
				}

				const isSameToken = token == jwt.getToken()

				if ( isSameToken )
				{
					return Promise.resolve()
				}

				jwt.setToken(token)

				dispatch(Types.START_TOKEN_TIMER)

				const { refreshed } = payload ?? {}

				if ( refreshed )
				{
					return Promise.resolve()
				}

				return new Promise
				(
					(resolve, reject) =>
					{
						dispatch(Types.SET_AUTH_USER)
							.then(resolve)
							.catch(reject)
					}
				)
			}

			return Promise.reject()
		},

		[Types.START_TOKEN_TIMER]({ commit }, payload)
		{
			return new Promise
			(
				(resolve, reject) =>
				{
					const delay    = jwt.expiresIn()
					const callback = ( payload?.callback ?? payload ) || ( () => window.location.reload() )

					if ( delay > 0 )
					{
						const timer = window.setTimeout(callback, delay)

						commit({ type: Types.TOKEN_TIMER, timer })
						resolve(timer)
					}
					else
					{
						reject(`Unable to start timer due to invalid delay ${delay}`)
					}
				}
			)
		},

		[Types.STOP_TOKEN_TIMER]({ commit })
		{
			commit({ type: Types.TOKEN_TIMER, timer: null })

			return Promise.resolve()
		},
	},
}